// Frontend-specific config - completely self-contained to avoid ESM/CJS mixing issues
//
// IMPORTANT: This file intentionally duplicates values from config.js rather than importing it.
// The inheritance was broken in Feb 2025 because the @hopdrive/sdk-outcomes package's ESM exports
// field caused webpack 4 to use stricter module resolution, breaking CommonJS/ESM interop.
// Mixing require('./config') (CJS) with export default (ESM) caused runtime errors:
// "Cannot assign to read only property 'exports'"
//
// If you need to update base config values (appEnv, firebase claims, etc.), you must update
// BOTH this file AND config.js to keep them in sync.
//
// See config.js for the backend version that still uses CommonJS.

const log = false;

const env = process.env.REACT_APP_ENV || 'test';

// Base config values (duplicated from config.js to avoid CJS import)
const baseConfig = {
  appEnv: env,
  name: process.env.PKG_NAME || 'hopdrive-admin',
  version: process.env.PKG_VERSION || '3.0.0',
  fullName: `${process.env.PKG_NAME}-v${process.env.PKG_VERSION}`,
  versionCheckFrequency: 3600000,

  // Firebase claims
  firebaseClaimsNamespace: 'https://hasura.io/jwt/claims',
  firebaseClaimsAllowedRegions: 'x-hasura-allowed-regions',
  firebaseClaimsAllowedRoles: 'x-hasura-allowed-roles',
  firebaseClaimsDefaultRole: 'x-hasura-default-role',
  firebaseClaimsAllowedCustomers: 'x-hasura-allowed-customers',
  firebaseClaimsAllowedPayers: 'x-hasura-allowed-payers',
  firebaseClaimsPayerChildCustomers: 'x-hasura-payer-child-customers',
  firebaseClaimsCustomerId: 'x-hasura-customer-id',
  firebaseClaimsUserId: 'x-hasura-user-id',
  firebaseClaimsDriverId: 'x-hasura-driver-id',
  firebaseClaimsUserEmail: 'x-hasura-user-email',

  // Google config
  googleClientUrl: 'https://clients3.google.com/generate_204',

  // Email config
  emailInfo: 'info@hopdrive.com',
  emailSupport: 'support@hopdrive.com',
};

// Base environment-specific config
const base_config_demo = {
  graphqlHttpUrl: 'https://gql-test.hopdrive.io/v1/graphql',
  graphqlWsUrl: 'wss://gql-test.hopdrive.io/v1/graphql',
  graphqlClientName: 'hopdrive-client',
  loggingEnableColoring: false,
  loggingEnabled: false,
  hopdriveAdminUrl: 'https://admin-test.hopdrive.io/',
  redcapResyncUrl: 'https://hd3-test.io/v1/webhook-background',
};

const base_config_test = {
  graphqlHttpUrl: 'https://gql-test.hopdrive.io/v1/graphql',
  graphqlWsUrl: 'wss://gql-test.hopdrive.io/v1/graphql',
  graphqlClientName: 'hopdrive-client',
  loggingEnableColoring: false,
  loggingEnabled: false,
  hopdriveAdminUrl: 'https://admin-test.hopdrive.io/',
  redcapResyncUrl: 'https://hd3-test.io/v1/webhook-background',
};

const base_config_prod = {
  graphqlHttpUrl: 'https://gql.hopdrive.io/v1/graphql',
  graphqlWsUrl: 'wss://gql.hopdrive.io/v1/graphql',
  graphqlClientName: 'hopdrive-client',
  loggingEnableColoring: false,
  loggingEnabled: false,
  hopdriveAdminUrl: 'https://admin.hopdrive.io/',
  redcapResyncUrl: 'https://hd3.io/v1/webhook-background',
};

// Try to load local config, use empty objects if not found
let local_base = {};
let local_frontend = {};
try {
  const localConfig = require('./config.local');
  local_base = localConfig.config_base || {};
  local_frontend = localConfig.config_frontend || {};
} catch (error) {
  log && console.log('No local config found, using empty config');
}

const config_demo = {
  // Demo-specific overrides if needed
};

const config_test = {
  // Test-specific subdomain config
  adminSubdomain: 'admin-test',
  auth0Subdomain: 'sat-auth-test',
  graphqlSubdomain: 'gql-test',
  tookanSubdomain: 'dispatch-api-test',
  returnsSubdomain: 'returns-test',
  conciergeSubdomain: 'concierge-test',
  eventsSubdomain: 'events-test',
  ridesSubdomain: 'hoprides-test',
  graphqlHttpUrl: 'https://gql-test.hopdrive.io/v1/graphql',
  graphqlWsUrl: 'wss://gql-test.hopdrive.io/v1/graphql',
  awsDocumentsBucket: 'documents-test.hopdrive.com',
};

const config_prod = {
  // Production-specific subdomain config
  adminSubdomain: 'admin',
  auth0Subdomain: 'sat-auth',
  graphqlSubdomain: 'gql-alb',
  tookanSubdomain: 'dispatch-api-prod',
  returnsSubdomain: 'returns',
  conciergeSubdomain: 'concierge',
  eventsSubdomain: 'events',
  ridesSubdomain: 'hoprides',
  graphqlHttpUrl: 'https://gql.hopdrive.io/v1/graphql',
  graphqlWsUrl: 'wss://gql.hopdrive.io/v1/graphql',
  awsDocumentsBucket: 'documents.hopdrive.com',
};

const config = {
  ...baseConfig,

  // Apply base environment-specific config
  ...base_config_test, //opinionated default to test
  ...(env === 'demo' ? base_config_demo : null),
  ...(env === 'test' ? base_config_test : null),
  ...(env === 'production' ? base_config_prod : null),

  // Firebase config
  firebaseApiKey: process.env.REACT_APP_FIREBASE_API_KEY || '',
  firebaseAppId: process.env.REACT_APP_FIREBASE_APP_ID || '',
  firebaseAuthDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || '',
  firebaseMeasurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || '',
  firebaseMessagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '',
  firebaseStorageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || '',
  firebaseProjectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || '',

  // iCheckGateway config
  icheckgatewayAppId: process.env.REACT_APP_IC_APP_ID || '',
  icheckgatewayAppSecret: process.env.REACT_APP_IC_APP_SEC || '',

  // Google config
  googleApiKey: process.env.REACT_APP_GOOGLE_API_KEY || '',
  googleMapsKey: process.env.REACT_APP_GOOGLE_KEY || '',
  googlePlacesKey: process.env.REACT_APP_GPLCS || '',

  // Auth0 config
  auth0ClientId: process.env.REACT_APP_A0_CID || '',

  // Accessorial codes
  accessorialCodes: 'delay,fuel,partial,waived,surge,tow,toll,ride,insurance,reimbursement',

  // Environment specific keys (frontend-specific)
  ...config_test, //opinionated default to test
  ...(env === 'demo' ? config_demo : null),
  ...(env === 'test' ? config_test : null),
  ...(env === 'production' ? config_prod : null),

  // Local must always be last to ensure it overrides all other configs
  ...local_base,
  ...local_frontend,
};

export default config;
